import { useAuthStore, type AvailableAccount } from "@/shared/state/authstore";
import type { Employment, MyProfileResponse } from "@/entities/employee/model/types";

export type RevokedAccount = { accountId: number; accountName: string; paused: boolean };

/**
 * Whether an employment row is one the person has been let go from, as opposed to one they have not
 * started yet. Employees are created inactive and only switched on at activation, so `isActive` alone
 * cannot say it: a new hire still being reviewed is inactive too, and must see the welcome flow.
 * Activation stamps `activatedAt` and deactivation never clears it, which is the backend's own
 * definition of "was employed here and no longer is" (`EmploymentAccess`).
 *
 * The login response cannot answer this: it reports `accessible: false` for a revoked account, but the
 * same value also covers a guard on the back-office, who was never revoked from anything.
 */
export function isEmploymentRevokedRow(employment: Pick<Employment, "isActive" | "activatedAt">): boolean {
    return !employment.isActive && employment.activatedAt !== null;
}

/** The organisations this person no longer has access to: what the profile says, plus what the API just refused. */
export function revokedAccountsOf(
    employments: Employment[] | undefined,
    availableAccounts: AvailableAccount[],
    refusedAccountIds: number[],
    pausedAccountIds: number[] = [],
): RevokedAccount[] {
    const found = new Map<number, string>();
    for (const e of employments ?? []) {
        if (isEmploymentRevokedRow(e)) found.set(e.accountId, e.accountName);
    }
    for (const id of refusedAccountIds) {
        if (!found.has(id)) {
            found.set(id, availableAccounts.find((a) => a.accountId === id)?.accountName ?? "");
        }
    }
    return [...found].map(([accountId, accountName]) => ({
        accountId,
        accountName,
        paused: pausedAccountIds.includes(accountId),
    }));
}

export function useRevokedAccounts(currentUser: MyProfileResponse | undefined): RevokedAccount[] {
    const availableAccounts = useAuthStore((s) => s.availableAccounts);
    const refusedAccountIds = useAuthStore((s) => s.revokedAccountIds);
    const pausedAccountIds = useAuthStore((s) => s.pausedAccountIds);
    return revokedAccountsOf(currentUser?.employments, availableAccounts, refusedAccountIds, pausedAccountIds);
}
